import { A } from '@adornis/base/env-info.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { BuchungsheldLitElement } from '@adornis/buchungsheld-components/BuchungsheldLitElement.js';
import { Institution } from '@adornis/buchungsheld-db/entities/institution.js';
import { Setting } from '@adornis/buchungsheld-db/entities/settings.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import { Permission } from '@adornis/buchungsheld-db/enums/roles.js';
import { TrDialog } from '@adornis/buchungsheld-theme/components/tr-dialog.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { permissionGuard } from '@adornis/user-forms/permission-guard.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { map, switchMap } from 'rxjs';
import { getAvailableInstitutions } from '../api/institution.js';

const insitutionSelectionSet = {
  _id: 1,
  logoID: 1,
  shortDescription: 1,
  name: 1,
  maintenanceMode: 1,
  isLDAPOnly: 1,
  sortingWeight: 1,
} as const;

/**
 * @element bh-admin-einrichtungen-list
 */
@customElement('bh-admin-einrichtungen-list')
export class BHAdminEinrichtungenList extends BuchungsheldLitElement {
  @property({ type: Boolean, attribute: 'show-admin' }) showAdmin = new RXController<Maybe<boolean>>(this, false);

  @state() institutions = new RXController(
    this,
    this.showAdmin.observable.pipe(
      map(showAdmin => (showAdmin ? 'EDIT' : 'ORDER_WORKSHOPS_FROM')),
      switchMap(lookupReason => getAvailableInstitutions(lookupReason)(insitutionSelectionSet)),
      map(institutions => institutions.toSorted((a, b) => (a.sortingWeight ?? 999999) - (b.sortingWeight ?? 999999))),
    ),
  );
  @state() private readonly _size = new WindowSizeController(this);

  @state() private readonly _currentUser = new RXController(this, currentUser<User>());

  override render() {
    if (this._currentUser.isLoading) return html` <x-loading-state></x-loading-state>`;

    return html`
      <x-flex space="lg">
        <tr-flex space="md">
          ${repeat(
            this.institutions.value ?? [],
            i => i._id,
            // eslint-disable-next-line complexity
            (institution, index) => {
              const isSuspended = !!(this._currentUser.value?.suspentions ?? [])
                .map(suspention => suspention.institutionID)
                .includes(institution._id);

              const imagePart = () => {
                if (institution.logoID) {
                  return html`
                    <x-image
                      disabled
                      .value=${AdornisFile.getServeLink(institution.logoID)}
                      mode="contain"
                      ${css({
                        width: `250px`,
                        height: '250px',
                        margin: '10px',
                      })}
                    ></x-image>
                  `;
                }

                return html`
                  <tr-flex
                    ${css({ minWidth: '250px', height: '250px', background: this.colors.accent.shade(200) })}
                    center
                    crossaxis-center
                  >
                    <tr-icon huge ${css({ color: '#fff', fontSize: '3em' })}> apartment </tr-icon>
                  </tr-flex>
                `;
              };

              const contentPart = () => {
                return html`
                  <tr-flex flex padding="md" space="md" space-between>
                    <tr-flex space="sm">
                      <x-h2> ${institution.name} </x-h2>
                      ${institution.shortDescription
                        ? html`
                            <x-text ${css({ lineWrap: 'break-line' })}>
                              ${institution.shortDescription.substring(0, 700)}${institution.shortDescription.length >
                              700
                                ? '...'
                                : ''}
                            </x-text>
                          `
                        : nothing}
                    </tr-flex>

                    <tr-flex space="xxs" horizontal wrap crossaxis-start>
                      ${isSuspended
                        ? html` <x-chip tone="error">${this._translation.translate('PROFILE', 'SUSPENDED')}</x-chip> `
                        : nothing}
                      ${institution.maintenanceMode
                        ? html`
                            <x-chip tone="warning"
                              >${this._translation.translate('admin_institution', 'maintenance_mode_active')}</x-chip
                            >
                          `
                        : nothing}
                      ${institution.isLDAPOnly
                        ? html`
                            <x-chip tone="subtle"
                              >${this._translation.translate('PROFILE', 'LDAP_ACCOUNT_REQUIRED')}</x-chip
                            >
                          `
                        : nothing}
                    </tr-flex>
                  </tr-flex>
                `;
              };

              return html`
                <tr-card
                  href=${this.showAdmin.value
                    ? `/admin/institution-details/${institution._id}/home`
                    : `/registration?step=workshop&institution=${institution._id}`}
                  ${css({
                    // width: hasContent ? '100%' : 'unset',
                    width: '100%',
                    pointerEvents:
                      !this.showAdmin.value && (institution.maintenanceMode || isSuspended) ? 'none' : 'initial',
                  })}
                >
                  <tr-flex
                    ?horizontal=${!this._size.mobile}
                    ?reverse=${index % 2 === 1 && !this._size.mobile}
                    ${css({
                      marginLeft: `-${this.spacing.md}`,
                      marginBottom: `-${this.spacing.md}`,
                      marginTop: `-${this.spacing.md}`,
                      marginRight: `-${this.spacing.md}`,
                      ...(!this._size.mobile ? { maxHeight: '250px' } : {}),

                      textOverflow: 'ellipsis',
                    })}
                  >
                    ${imagePart()} ${contentPart()}
                  </tr-flex>
                </tr-card>
              `;
            },
          )}
          ${this.showAdmin.value ? this._addInstitutionCard() : nothing}
        </tr-flex>
      </x-flex>
    `;
  }

  private _addInstitutionCard() {
    return permissionGuard(
      Permission.ADD_INSTITUTIONS,
      () => html`
        <tr-card
          href="#"
          ${css({ width: '100%', color: this.colors.accent, height: '250px' })}
          center
          @click=${async () => {
            try {
              const institutionID = A.getGloballyUniqueID();
              const name = this._translation.translate('INSTITUTIONS', 'newInstitution');
              const institution = new Institution({ _id: institutionID, name });

              const action = async () => {
                const settings = new Setting({ institutionID });
                await settings.create();
                await institution.create();
              };

              await TrDialog.waitFor(action(), this._translation.translate('INSTITUTIONS', 'addingNewInstitution'));

              goTo(`/admin/institution-details/${institution._id}/settings`);
              void XSnackbar.show(this._translation.translate('INSTITUTIONS', 'addedNewInstitution'));
              await getCoreDataClassForInstitution(institution._id);
            } catch (e) {
              console.error(e);
              void XSnackbar.show(`Fehler: ${(e as Error).message}`);
            }
          }}
        >
          <tr-flex space="md" center crossaxis-center ${css({ fontSize: this.spacing.xxl })}>
            <tr-icon huge> add </tr-icon>
            <tr-h3 align="center"> ${this._translation.translate('INSTITUTIONS', 'addNewInstitution')} </tr-h3>
          </tr-flex>
        </tr-card>
      `,
      { forbiddenTemplate: nothing },
    );
  }
}
